import { useEffect } from 'react';
import { SITE_NAME, absoluteUrl } from './site';

function setTag(selector: string, create: () => HTMLElement, value: string) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  if (el.tagName === 'LINK') el.setAttribute('href', value);
  else el.setAttribute('content', value);
}

/**
 * Keeps the document head in step with client-side navigation.
 *
 * Crawlers read the prerendered HTML for each URL, so this is not what makes
 * the site indexable — it is what stops a player who navigated in-app from
 * bookmarking or sharing a page that still claims to be the hub.
 */
export function useMeta({
  title,
  description,
  path,
  image,
}: {
  title: string;
  description?: string;
  /** Root-relative path of the current page, for the canonical URL. */
  path: string;
  image?: string;
}) {
  useEffect(() => {
    const full = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    document.title = full;

    if (description) {
      setTag(
        'meta[name="description"]',
        () => Object.assign(document.createElement('meta'), { name: 'description' }),
        description,
      );
      setTag(
        'meta[property="og:description"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:description');
          return m;
        },
        description,
      );
    }

    setTag(
      'link[rel="canonical"]',
      () => Object.assign(document.createElement('link'), { rel: 'canonical' }),
      absoluteUrl(path),
    );
    setTag(
      'meta[property="og:url"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:url');
        return m;
      },
      absoluteUrl(path),
    );
    setTag(
      'meta[property="og:title"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:title');
        return m;
      },
      full,
    );
    if (image) {
      setTag(
        'meta[property="og:image"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:image');
          return m;
        },
        absoluteUrl(image),
      );
    }
  }, [title, description, path, image]);
}
